import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';

import View from './view';
import { actions, selectors } from '../../redux';
import {
  ANSWERS__POST,
  PAGES__ROLE_ERROR,
  RESULT__FINALISE,
  ELEMENTS__SEND_UPLOAD,
  SURVEYS__FETCH_ACTIVE,
  CURRENCIES__FETCH,
  RESULT__FETCH,
} from '../../redux/constants';
import { pipe, bugsnagLeaveBreadcrumb, recaptchaTokenize, SURVEY_INIT_ACTION } from '../../util';
import { participantErrorRegistry } from '../../redux/error-registry';
import { selectPageError } from '../../../researcher/redux/errors/registry.errors';
import { setLocale } from '../../../../translations/i18n';

import { withMedia } from '../../../researcher/blocks/media';
import { useMediaContext } from '../../../researcher/blocks/media/context';

const swallowApiError = (error) => {
  if (error?.name === 'AbortError') return null;
  if (!error?.isAxiosError && !error?.response) throw error;
  return null;
};

export const computeShowPreloader = ({
  criticalBusy,
  initialized,
  pageError,
  hasError,
  isDisqualified,
  survey,
  currentPage,
}) =>
  !pageError &&
  !hasError &&
  !isDisqualified &&
  ((criticalBusy && !initialized) || !survey || !currentPage);

const SurveyClientController = (props) => {
  const {
    isPreview,
    fetchPreviewData,
    fetchTakeSurveyData,
    createImpression,
    currentPage,
    currentPageId,
    survey,
    surveyClient,
    showPreloader,
  } = props;

  const [ready, setReady] = useState(false);
  const prevPageIdRef = useRef(null);
  const prevTokenRef = useRef(null);
  const initKeyRef = useRef(null);

  useEffect(() => {
    const initKey = `${isPreview}:${window.location.pathname}`;
    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;
    const action = isPreview ? fetchPreviewData : fetchTakeSurveyData;
    action();
    setReady(true);
    // eslint-disable-next-line
  }, [isPreview, fetchPreviewData, fetchTakeSurveyData]);

  useEffect(() => {
    const prevPageId = prevPageIdRef.current;
    const prevToken = prevTokenRef.current;
    const token = get(surveyClient, 'token');
    const key = `${currentPageId}${token}`;
    const prevKey = `${prevPageId}${prevToken}`;
    if (token && currentPageId && currentPage && key !== prevKey) {
      const { header, position } = currentPage;
      const text = get(header, 'properties.text', 'Untitled Slide');
      const breadCrumb = `Slide ${position}: ${text}`;
      bugsnagLeaveBreadcrumb(breadCrumb, { id: currentPageId });
      createImpression({
        pageId: currentPageId,
        token,
      });
    }
    prevPageIdRef.current = currentPageId;
    prevTokenRef.current = token;
    // eslint-disable-next-line
  }, [currentPageId, currentPage, surveyClient, createImpression]);

  useEffect(() => {
    if (survey) {
      setLocale(survey?.enabledLocale || 'en');
    }
  }, [survey?.enabledLocale]);

  const { media } = useMediaContext();

  return <View {...props} phone={media.phone} showPreloader={ready ? showPreloader : true} />;
};

SurveyClientController.propTypes = {
  isPreview: PropTypes.bool.isRequired,
  currentPage: PropTypes.shape(),
  currentPageId: PropTypes.string,
  fetchPreviewData: PropTypes.func.isRequired,
  fetchTakeSurveyData: PropTypes.func.isRequired,
  showPreloader: PropTypes.bool.isRequired,
  surveyClient: PropTypes.shape().isRequired,
  createImpression: PropTypes.func.isRequired,
  redirect: PropTypes.func.isRequired,
};

SurveyClientController.defaultProps = {
  currentPage: null,
  currentPageId: null,
};

const mapStateToProps = (state, props) => {
  const surveyClient = selectors.selectSurveyClient(state);
  const result = selectors.selectResult(state);
  const lyticsAccountId = (surveyClient && surveyClient.lyticsAccountId) || null;

  const pageError = selectPageError(state, participantErrorRegistry);
  const pages = selectors.selectClientPages(state, surveyClient, result, pageError);
  const currentPageIndex = selectors.selectCurrentPageIndex(pages, result);
  const currentPageId = get(pages, [currentPageIndex, 'id']);

  const currentPage = selectors.selectPage(state, pages, currentPageId, result);
  const answersBusy =
    selectors.apiClientCountRequests(state, [ANSWERS__POST, ELEMENTS__SEND_UPLOAD]) > 0;
  const finaliserBusy = selectors.apiClientHasRequest(state, RESULT__FINALISE);
  const { surveyId } = result;
  const survey = selectors.selectSurveyById(state, surveyId);
  const isSurveyInactive = selectors.selectSurveyIsInactive(state, props.params.surveyToken);
  const themeClass = selectors.getThemeClass(survey);
  const participant = selectors.selectParticipant(state);
  const rewardCode = selectors.selectRewardCode(state);
  const hasAnswers = selectors.hasAnswer(state, currentPage);

  const lastPageIndex = pages.length - 1;
  const progress = lastPageIndex > 0 ? Math.round((currentPageIndex / lastPageIndex) * 100) : 0;

  const hasError = get(currentPage, '$role') === PAGES__ROLE_ERROR;
  const isTestMode = props.router.location.query['test-mode'] === 'true';
  const isDisqualified = result.state === 'disqualified';
  const disqualification = result.disqualification || null;

  const data = {
    answersBusy,
    currentPage,
    currentPageId,
    currentPageIndex,
    disqualification,
    hasAnswers,
    hasError,
    isDisqualified,
    isSurveyInactive,
    finaliserBusy,
    lyticsAccountId,
    pages,
    participant,
    progress,
    result,
    rewardCode,
    showPreviousButton: Boolean(surveyClient.showPreviousButton),
    survey,
    surveyClient,
    themeClass,
    isPreview: 'organizationId' in props.params,
    isTestMode,
    selectElement: (elementId) => selectors.selectElementById(state, elementId),
    selectElementsByParentId: (parentId) => selectors.selectElementsForClient(state, parentId),
    showPreloader: computeShowPreloader({
      criticalBusy:
        selectors.apiClientCountRequests(state, participantErrorRegistry.criticalTypes) > 0,
      initialized: participantErrorRegistry.selectIsInitialized(state),
      pageError,
      hasError,
      isDisqualified,
      survey,
      currentPage,
    }),
  };

  return data;
};

export const mapDispatchToProps = (dispatch, props) => {
  const { organizationId, surveyId, surveyToken, invitationToken } = props.params;
  const isTestMode = props.router.location.query['test-mode'] === 'true';

  return {
    fetchTakeSurveyData: async () => {
      const init = get(window, 'beagly.init');
      const hydratedSurvey = Boolean(init && init.survey);
      const hydratedResult = hydratedSurvey && Boolean(init.result);
      const recaptchaPromise = hydratedResult ? null : recaptchaTokenize(SURVEY_INIT_ACTION);

      let active;
      if (hydratedSurvey) {
        dispatch({ type: SURVEYS__FETCH_ACTIVE, data: init.survey });
        dispatch({ type: CURRENCIES__FETCH, data: init.currencies });
        if (hydratedResult) {
          dispatch({ type: RESULT__FETCH, data: init.result });
        }
        active = init.survey;
      } else {
        active = await dispatch(actions.surveysFetchActive(surveyToken)).catch(swallowApiError);
      }

      if (active?.survey?.status === 'active') {
        if (!hydratedResult) {
          const recaptchaToken = await recaptchaPromise;
          const [resultResponse] = await Promise.all([
            dispatch(
              actions.resultInit({ surveyId: active.survey.id, isTestMode, recaptchaToken }),
            ).catch(swallowApiError),
            hydratedSurvey ? null : dispatch(actions.currenciesFetch()).catch(swallowApiError),
          ]);
          const resultVersion = resultResponse?.result?.version;
          if (resultVersion && active.version && resultVersion !== active.version) {
            const resultToken =
              resultResponse.token ||
              dispatch((_, getState) => selectors.selectSurveyClient(getState()).token);
            if (resultToken) {
              await dispatch(actions.surveysFetchActive(surveyToken, resultToken)).catch(
                swallowApiError,
              );
            }
          }
        }
        dispatch(actions.anwsersFetch()).catch(swallowApiError);
      }
    },
    fetchPreviewData: async () => {
      await Promise.all([
        dispatch(actions.resultInitPreview(surveyId)).catch(swallowApiError),
        dispatch(actions.pagesFetch(organizationId, surveyId)).catch(swallowApiError),
        dispatch(actions.elementsFetch(organizationId, surveyId)).catch(swallowApiError),
        dispatch(actions.surveysFetchItem(organizationId, surveyId)).catch(swallowApiError),
      ]);
      dispatch(actions.anwsersFetch()).catch(swallowApiError);
    },
    finalise: (token) => dispatch(actions.surveyClientFinalise(token, invitationToken)),
    redirect: async (token, redirectLink) => {
      const action = actions.surveyClientRedirect(token, invitationToken, redirectLink);
      await dispatch(action);
      window.top.location = redirectLink;
    },
    issueImmediateReward: pipe(actions.issueImmediateReward, dispatch),
    createImpression: pipe(actions.participantCreateImpression, dispatch),
    setAuthEmail: pipe(actions.participantUseEmail, dispatch),
    sendUpload: pipe(actions.elementsSendUpload, dispatch),
    toggleProgress: () => dispatch(actions.surveyClientToggleProgress),
    authorizeInPreviewMode: pipe(actions.authorizeInPreviewMode, dispatch),
    resetAuth: pipe(actions.participantReset, dispatch),
    submitAnswers: async ({
      //  hasAnswers,
      token,
      answers,
      page,
      event,
    }) => {
      const isPrevButton = event.nativeEvent.submitter?.value === 'previous';
      const hasElements = page.type === 'Page';
      // const shouldSubmit = (!isPrevButton || (isPrevButton && hasAnswers)) && hasElements;
      const shouldSubmit = hasElements;

      if (shouldSubmit) {
        await dispatch(actions.anwsersSubmit(token, answers, page.id));
        dispatch(actions.anwsersFetch()).catch(swallowApiError);
      }

      if (isPrevButton) {
        const offset = shouldSubmit ? 2 : 1;
        const prevPageAction = actions.surveyClientPrevPage(offset);
        dispatch(prevPageAction);
      }
    },
  };
};

export default withMedia(connect(mapStateToProps, mapDispatchToProps)(SurveyClientController));

/**
 * @see docs/worklog/maintenance/20093-investigate-facebook-auth-errors/rfc-frontend.md
 */
